import React, { useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  ChartOptions,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import styled from "styled-components";
import { PlanesActivosPorDia } from "../../../services/analisisOperaciones";
import { useEstadisticasNumericas } from "../../../hooks/estadisticasNumericas";
import {
  dateCompareStr,
  formatearFechaCortaStr,
} from "../../../utils/dateUtils";
import { divertidogsTheme } from "../../../styles/divertidogs-theme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

interface PlanesActivosPorDiaChartProps {
  planesGuarderiaPorDia: PlanesActivosPorDia[];
}

function PlanesActivosPorDiaChart({
  planesGuarderiaPorDia,
}: PlanesActivosPorDiaChartProps) {
  const guarderiaColor = divertidogsTheme.colors.main;
  const promedioColor = divertidogsTheme.colors.info;
  const minimoColor = divertidogsTheme.colors.error;
  const maximoColor = divertidogsTheme.colors.success;
  const estadisticas = useEstadisticasNumericas(
    planesGuarderiaPorDia,
    (item) => item.conteo
  );

  const chartData = useMemo(() => {
    const fechas = Array.from(
      new Set(planesGuarderiaPorDia.map((item) => item.fecha))
    ).sort(dateCompareStr);

    const conteosGuarderia = new Map(
      planesGuarderiaPorDia.map((item) => [item.fecha, item.conteo])
    );

    return {
      labels: fechas.map((fecha) => formatearFechaCortaStr(fecha)),
      datasets: [
        {
          label: "Guardería",
          data: fechas.map((fecha) => conteosGuarderia.get(fecha) ?? 0),
          borderColor: guarderiaColor,
          backgroundColor: `${guarderiaColor}20`,
          pointRadius: 4,
          pointBackgroundColor: guarderiaColor,
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          tension: 0,
        },
      ],
    };
  }, [guarderiaColor, planesGuarderiaPorDia]);

  const formatearConteo = (valor: number) => Math.round(valor).toString();

  const chartOptions: ChartOptions<"line"> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Planes activos por día",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
      annotation: estadisticas && planesGuarderiaPorDia.length > 1
        ? {
            annotations: {
              promedio: {
                type: "line",
                yMin: estadisticas.promedio,
                yMax: estadisticas.promedio,
                borderColor: promedioColor,
                borderDash: [6, 6],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: promedioColor,
                  content: `Prom: ${formatearConteo(estadisticas.promedio)}`,
                },
              },
              minimo: {
                type: "line",
                yMin: estadisticas.minimo,
                yMax: estadisticas.minimo,
                borderColor: minimoColor,
                borderDash: [2, 4],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: minimoColor,
                  content: `Min: ${formatearConteo(estadisticas.minimo)}`,
                },
              },
              maximo: {
                type: "line",
                yMin: estadisticas.maximo,
                yMax: estadisticas.maximo,
                borderColor: maximoColor,
                borderDash: [2, 4],
                borderWidth: 2,
                label: {
                  display: true,
                  position: "end",
                  backgroundColor: maximoColor,
                  content: `Max: ${formatearConteo(estadisticas.maximo)}`,
                },
              },
            },
          }
        : undefined,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  }), [
    estadisticas,
    planesGuarderiaPorDia.length,
    promedioColor,
    minimoColor,
    maximoColor,
  ]);

  return (
    <ChartContainer>
      <Line data={chartData} options={chartOptions} />
    </ChartContainer>
  );
}

const ChartContainer = styled.div`
  height: 100%;
  width: 100%;
`;

export default React.memo(PlanesActivosPorDiaChart);
